"use client";
import { useRouter } from "next/navigation";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useSoundEffects } from "@thehoneyjar/sound-engine";
import { useActiveTetrisEvent } from "@/hooks/tetris/use-active-event";
import { useEventCountdown } from "@/hooks/tetris/use-event-countdown";
import {
  getHibernationTickerItems,
  isLobbyHibernating,
} from "@/components/lobby/disabled-ticker-source";

function formatTickerCountdown(seconds: number | null): string | null {
  if (seconds === null || Number.isNaN(seconds)) return null;
  const value = Math.max(0, seconds);
  const days = Math.floor(value / 86400);
  const hours = Math.floor((value % 86400) / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const secs = value % 60;
  if (days > 0) {
    return `${days}d ${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

// Memoized ticker component
export const EnhancedLiveTicker = memo(({ speed = 60 }: { speed?: number }) => {
  const router = useRouter();
  const { playSound } = useSoundEffects();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<Animation | null>(null);
  const [dupCount, setDupCount] = useState(2);
  const { data: activeEvent } = useActiveTetrisEvent();
  const eventCountdown = useEventCountdown(activeEvent ?? null);

  // Anchor event — BGT discontinuation banner (always shown).
  const baseEvents = useMemo(
    () => [
      {
        text: "⚠️ IMPORTANT: After Berachain Foundation's recent update, Henlockers will no longer receive Reward Vault rewards. LEARN MORE →",
        link: "https://forum.berachain.com/t/pol-update-inflation-reduction-and-rewards-vault-realignment/259",
      },
    ],
    [],
  );

  // Hibernation flavor mixes in when ≥1 surface is disabled. Anchor stays
  // first so the BGT discontinuation banner doesn't get buried.
  const hibernationItems = useMemo(
    () => (isLobbyHibernating() ? getHibernationTickerItems() : []),
    [],
  );

  const countdownLabel = formatTickerCountdown(eventCountdown);

  const events = useMemo(() => {
    if (activeEvent && countdownLabel) {
      return [
        {
          text: `🏆 ${activeEvent.name} — ends in ${countdownLabel}`,
          link: "/arcade",
        },
        ...baseEvents,
        ...hibernationItems,
      ];
    }
    return [...baseEvents, ...hibernationItems];
  }, [baseEvents, hibernationItems, activeEvent, countdownLabel]);

  const handleTickerClick = (item: { link?: string }) => {
    if (!item.link) return;

    // Play henlo-style sound effect
    void playSound("buttonClickGuts");

    // Navigate appropriately
    if (item.link.startsWith("http")) {
      window.open(item.link, "_blank", "noopener,noreferrer");
    } else {
      router.push(item.link);
    }
  };

  // Duplicate items N times for seamless WAAPI loop, adjusted to container size
  const duplicatedItems = useMemo(() => {
    return Array.from({ length: dupCount }).flatMap(() => events);
  }, [events, dupCount]);

  // Measure content and animate with Web Animations API for smooth, seamless scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const content = container.querySelector(".marquee-content") as HTMLElement | null;
    if (!content) return;

    const measureAndAnimate = () => {
      const totalWidth = content.scrollWidth; // width of all duplicates
      if (totalWidth <= 0 || dupCount <= 0) return;

      const containerWidth = container.clientWidth;
      const singleWidth = totalWidth / dupCount; // width of one sequence
      if (singleWidth <= 0) return;

      // Determine how many full sequences we should scroll to ensure
      // we travel at least one container width, and wrap seamlessly.
      const multiples = Math.max(1, Math.ceil(containerWidth / singleWidth));

      // Ensure we have enough duplicates to cover the travel distance + viewport
      const desiredDup = Math.max(2, multiples + 1);
      if (desiredDup !== dupCount) {
        setDupCount(desiredDup);
        return; // re-run after dupCount updates
      }

      const travelDistance = multiples * singleWidth; // multiple of singleWidth for seamless reset

      // Speed: pixels per second (configurable)
      const pixelsPerSecond = Math.max(10, speed);
      const duration = (travelDistance / pixelsPerSecond) * 1000; // ms

      // Cancel any previous animation
      if (animationRef.current) {
        animationRef.current.cancel();
        animationRef.current = null;
      }

      const animation = content.animate(
        [{ transform: "translateX(0)" }, { transform: `translateX(-${travelDistance}px)` }],
        {
          duration,
          iterations: Infinity,
          easing: "linear",
        },
      );
      animationRef.current = animation;
    };

    measureAndAnimate();

    // Recalculate on resize to keep it smooth
    const resizeObserver = new ResizeObserver(() => measureAndAnimate());
    resizeObserver.observe(container);
    resizeObserver.observe(content);

    return () => {
      resizeObserver.disconnect();
      if (animationRef.current) {
        animationRef.current.cancel();
        animationRef.current = null;
      }
    };
  }, [speed, dupCount]);

  return (
    <div className="relative overflow-hidden" style={{ paddingLeft: "80px", paddingRight: "80px" }}>
      <div ref={containerRef} className="relative w-full" style={{ willChange: "transform" }}>
        <div
          className="marquee-content flex gap-8 whitespace-nowrap"
          style={{ transform: "translateZ(0)", backfaceVisibility: "hidden" }}
        >
          {duplicatedItems.map((item, index) => (
            <TickerItemComponent
              key={`${index}-${(item as any).text}`}
              index={index}
              item={item}
              totalItems={duplicatedItems.length}
              onClick={() => handleTickerClick(item as any)}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

// Separate component for ticker items with optimized animations
const TickerItemComponent = memo(
  ({
    item,
    index,
    totalItems,
    onClick,
  }: {
    item: any;
    index: number;
    totalItems: number;
    onClick: () => void;
  }) => {
    // Rotate flicker variant by index so adjacent ticker items don't
    // sync up. Three variants → mod 3.
    const flickerVariant = ["a", "b", "c"][index % 3];
    return (
      <span
        className={`ticker-item-kansei kansei-flicker-${flickerVariant} font-pressStart2P text-[8px] uppercase ${
          item.link ? "hover:text-zinc-300 cursor-pointer text-zinc-400" : "text-zinc-500"
        }`}
        onClick={onClick}
        style={{
          // KANSEI: stepped opacity transition only — no scale spring, no
          // active:jiggle. Marquee text moondust zinc (was bright white,
          // operator: "too alive with white text"). Each item flickers
          // dying on a different cycle for desync.
          textShadow: "0 0 1px rgba(255,255,255,0.18)",
          transition: "opacity 166ms steps(4), color 166ms steps(4)",
        }}
      >
        {item.text}
      </span>
    );
  },
);

TickerItemComponent.displayName = "TickerItemComponent";
EnhancedLiveTicker.displayName = "EnhancedLiveTicker";
